// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
import { AppStatesEnumeration } from 'enumerations/app-states.enumeration';
import { UploadIdDecisionsEnumeration } from '../enumerations/upload-id-decisions.enumeration';
// Resolvers
import {
  getOCRCustomerDataResolver,
  saveDifferentDataDecisionResolver,
} from '../resolvers/upload-id-different-data.resolver';
// Translations
import { CONTINUE } from './translations/upload-id-different-data.handlers.translations';
// Types
import {
  UploadIdDifferentDataAcceptBuilderHandlersType,
  UploadIdDifferentDataBuilderHandlersType,
  UploadIdDifferentDataButtonConfigurationHandlersType,
  UploadIdDifferentDataMethodsHandlersType,
  UploadIdDifferentDataSaveBuilderHandlersType,
} from './types/upload-id-different-data.handlers.type';
import { UploadIdDifferentDataCustomerApiResponseType } from '../types/upload-id-different-data-customer-api-response.type';

const updateButtonWithFetchingState =
  () =>
  (
    previousConfiguration: UploadIdDifferentDataButtonConfigurationHandlersType
  ): UploadIdDifferentDataButtonConfigurationHandlersType => ({
    ...previousConfiguration,
    disabled: true,
    loading: true,
  });

const saveDecisionOCRCustomerDataEventHandler = async ({
  decision,
  initialValidationId,
  validationId,
  setCurrentState,
  setFetching,
  setError,
  handleContinueButtonClickTracking,
}: Omit<
  UploadIdDifferentDataSaveBuilderHandlersType,
  'setMainButtonConfiguration' | 'handleUploadAgainButtonClickTracking'
>): Promise<void> => {
  setFetching(true);
  handleContinueButtonClickTracking();

  const [, error] = await saveDifferentDataDecisionResolver({
    status: decision,
    ocrValidationId: initialValidationId || validationId || '',
  });

  if (error) {
    setError(AppErrorEnumeration.SAVE_DIFFERENT_DATA_FAILED);
    setCurrentState(AppStatesEnumeration.ERROR);
  } else {
    setCurrentState(AppStatesEnumeration.LOADING);
  }
};

const acceptOCRCustomerDataButtonClickHandler = async ({
  setMainButtonConfiguration,
  handleContinueButtonClickTracking,
  ...props
}: UploadIdDifferentDataAcceptBuilderHandlersType): Promise<void> => {
  setMainButtonConfiguration(updateButtonWithFetchingState());
  saveDecisionOCRCustomerDataEventHandler({
    ...props,
    handleContinueButtonClickTracking,
    decision: UploadIdDecisionsEnumeration.ACCEPTED,
  });
};

const setupUploadIdDifferentDataButtonConfigurationHandler = ({
  formatMessage,
  setMainButtonConfiguration,
  ...restProps
}: Omit<UploadIdDifferentDataBuilderHandlersType, 'setCustomerData'>): void => {
  setMainButtonConfiguration({
    children: formatMessage({ id: CONTINUE }),
    onClick: () =>
      acceptOCRCustomerDataButtonClickHandler({ ...restProps, setMainButtonConfiguration }),
  });
};

const uploadAgainDocumentsLinkClickHandler = ({
  handleUploadAgainButtonClickTracking,
  setMainButtonConfiguration,
  ...props
}: Omit<UploadIdDifferentDataSaveBuilderHandlersType, 'decision'>): void => {
  handleUploadAgainButtonClickTracking();
  setMainButtonConfiguration(updateButtonWithFetchingState());
  saveDecisionOCRCustomerDataEventHandler({
    ...props,
    decision: UploadIdDecisionsEnumeration.REJECTED,
  });
};

const fetchOCRCustomerDataEventHandler = async ({
  initialValidationId,
  setFetching,
  setCustomerData,
  setError,
  validationId,
  ...props
}: UploadIdDifferentDataBuilderHandlersType): Promise<void> => {
  const id = initialValidationId || validationId || '';

  if (id) {
    setFetching(true);

    const [customerData, error] = await getOCRCustomerDataResolver(id);

    if (error) {
      setFetching(false);
      setError(AppErrorEnumeration.FETCH_OCR_DATA_FAILED);
    } else {
      setCustomerData(customerData as UploadIdDifferentDataCustomerApiResponseType);
      setupUploadIdDifferentDataButtonConfigurationHandler({
        ...props,
        validationId: id,
        setFetching,
        setError,
      });
      setFetching(false);
    }
  } else {
    setError(AppErrorEnumeration.NO_VALIDATION_ID_ERROR);
  }
};

const UploadIdDifferentDataHandlers = ({
  formatMessage,
  setCustomerData,
  ...restProps
}: UploadIdDifferentDataBuilderHandlersType): UploadIdDifferentDataMethodsHandlersType => ({
  handleFetchOCRCustomerDataEvent: () =>
    fetchOCRCustomerDataEventHandler({
      ...restProps,
      formatMessage,
      setCustomerData,
    }),
  handleUploadAgainDocumentsLinkClick: () => uploadAgainDocumentsLinkClickHandler(restProps),
});

export { UploadIdDifferentDataHandlers };
